import { useEffect, useState, MouseEvent } from "react";
import { Flex, Box, Text } from "rebass/styled-components";
import styled from "styled-components";
import { useSetRecoilState } from "recoil";
import { BiX } from "react-icons/bi";

import { analytics } from "MVVM/Analytics";
import { useUser } from "MVVM/Hooks/useUser";
import { useShouldShowSubscriptionOverhaul } from "MVVM/Hooks/useSubscriptionOverhaul";
import { StripePaystationState } from "MVVM/State";
import { ReactComponent as ProIconSVG } from "MVVM/Assets/Avatar/Icons/pro.svg";
import { ReactComponent as PlatinumIconSVG } from "MVVM/Assets/Avatar/Icons/platinum.svg";
import { ReactComponent as EssentialsIconSVG } from "MVVM/Assets/Avatar/Icons/essentials.svg";
import {
  TIER_PRICING,
  ANNUAL_STRIPE_LOOKUP,
  WINBACK_MONTHLY_COUPON,
  WINBACK_ANNUAL_COUPON,
  WINBACK_ANNUAL_DISCOUNT,
  MONTHLY_STRIPE_LOOKUP,
  getTierAccentColor,
  getTierButtonGradient,
  getTierHeaderGradient,
  getTierBorderColor,
  getTierGlowColor,
} from "MVVM/Utilities/SubscriptionTiers";

const ELIGIBLE_TYPES = ["ESSENTIALS", "PRO", "CREATOR_PLAT"];
const LOCAL_STORAGE_KEY = "expired-upsell-dismissed";

const getTierIcon = (type: string) => {
  switch (type) {
    case "ESSENTIALS":
      return <EssentialsIconSVG />;
    case "PRO":
      return <ProIconSVG />;
    case "CREATOR_PLAT":
      return <PlatinumIconSVG />;
    default:
      return null;
  }
};

const computeDiscountedPrice = (
  priceStr: string,
  discountPercent: number,
): string => {
  const numeric = parseFloat(priceStr.replace(/[^0-9.]/g, ""));
  if (isNaN(numeric)) return priceStr;
  const discounted = numeric * (1 - discountPercent / 100);
  return `$${discounted.toFixed(2)}`;
};

const ExpiredSubscriptionUpsell = () => {
  const [isOpen, setIsOpen] = useState(false);
  const { allstarUser } = useUser();
  const setPaystation = useSetRecoilState(StripePaystationState);
  const showOverhaul = useShouldShowSubscriptionOverhaul();

  const userGroup = allstarUser?.user?.userGroup;
  const loggedIn = allstarUser?.loggedIn;
  const isAllstarEmail =
    allstarUser?.decoded?.sub?.endsWith("@allstar.gg") ?? false;

  useEffect(() => {
    if (!showOverhaul || !loggedIn || !userGroup) return;
    const { active, type } = userGroup;

    if (!isAllstarEmail) {
      if (localStorage.getItem(LOCAL_STORAGE_KEY)) return;
      if (active) return;
      if (!ELIGIBLE_TYPES.includes(type)) return;
    }

    setIsOpen(true);
    analytics.track("Expired Upsell - Viewed", { tier: type });
  }, [showOverhaul, loggedIn, userGroup, isAllstarEmail]);

  if (!isOpen || !userGroup) return <></>;

  const type = ELIGIBLE_TYPES.includes(userGroup.type) ? userGroup.type : "PRO";
  const pricing = TIER_PRICING[type];
  const accentColor = getTierAccentColor(type);
  const annualDiscount = WINBACK_ANNUAL_DISCOUNT[type] ?? 0;
  const discountedMonthly = computeDiscountedPrice(pricing.monthly, 15);
  const discountedAnnual = computeDiscountedPrice(
    pricing.annual,
    annualDiscount,
  );

  const dismiss = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
    analytics.track("Expired Upsell - Dismissed", { tier: type });
  };

  const handleMonthly = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
    analytics.track("Expired Upsell - Monthly Clicked", { tier: type });
    setPaystation((prev) => ({
      ...prev,
      isModalOpen: true,
      type: "subscribe",
      lookUp: MONTHLY_STRIPE_LOOKUP[type],
      couponId: WINBACK_MONTHLY_COUPON,
    }));
  };

  const handleAnnual = () => {
    localStorage.setItem(LOCAL_STORAGE_KEY, "true");
    setIsOpen(false);
    analytics.track("Expired Upsell - Annual Clicked", { tier: type });
    setPaystation((prev) => ({
      ...prev,
      isModalOpen: true,
      type: "subscribe",
      lookUp: ANNUAL_STRIPE_LOOKUP[type],
      couponId: WINBACK_ANNUAL_COUPON[type],
    }));
  };

  return (
    <ModalOverlay onClick={dismiss}>
      <ModalContainer
        $borderColor={getTierBorderColor(type)}
        $glowColor={getTierGlowColor(type)}
        onClick={(e: MouseEvent) => e.stopPropagation()}
      >
        <CloseButton onClick={dismiss}>
          <BiX size={24} />
        </CloseButton>
        <CardHeader $gradient={getTierHeaderGradient(type)}>
          <BackgroundBadgeWrapper>{getTierIcon(type)}</BackgroundBadgeWrapper>
          <LimitedOfferBadge $color={accentColor}>
            Limited Offer
          </LimitedOfferBadge>
          <Title>Welcome back — exclusive offer</Title>
        </CardHeader>
        <ModalContent>
          <PlanCards>
            <PlanCard>
              <PlanHeadlineRow>
                <PlanHeadline>Monthly</PlanHeadline>
                <DiscountTag $color={accentColor}>-15%</DiscountTag>
              </PlanHeadlineRow>
              <PricingInlineRow>
                <OriginalPrice>{pricing.monthly}</OriginalPrice>
                <PlanPeriod>/mo</PlanPeriod>
                <PricingArrow>→</PricingArrow>
                <DiscountedPrice>{discountedMonthly}</DiscountedPrice>
                <PlanPeriod>/mo</PlanPeriod>
              </PricingInlineRow>
              <PlanButton
                $gradient={getTierButtonGradient(type)}
                onClick={handleMonthly}
              >
                Renew Monthly
              </PlanButton>
            </PlanCard>
            <PlanDivider />
            <PlanCard>
              <PlanHeadlineRow>
                <PlanHeadline>Annual</PlanHeadline>
                <DiscountTag $color={accentColor}>
                  -{annualDiscount}%
                </DiscountTag>
              </PlanHeadlineRow>
              <PricingInlineRow>
                <OriginalPrice>{pricing.annual}</OriginalPrice>
                <PlanPeriod>/mo</PlanPeriod>
                <PricingArrow>→</PricingArrow>
                <DiscountedPrice>{discountedAnnual}</DiscountedPrice>
                <PlanPeriod>/mo</PlanPeriod>
              </PricingInlineRow>
              <PlanButton
                $gradient={getTierButtonGradient(type)}
                onClick={handleAnnual}
              >
                Renew Annual
              </PlanButton>
            </PlanCard>
          </PlanCards>
          <SecondaryButton onClick={dismiss}>Maybe later</SecondaryButton>
        </ModalContent>
      </ModalContainer>
    </ModalOverlay>
  );
};

const ModalOverlay = styled(Box)`
  align-items: center;
  background: rgba(0, 0, 0, 0.6);
  display: flex;
  height: 100%;
  justify-content: center;
  left: 0;
  position: fixed;
  top: 0;
  width: 100%;
  z-index: 1000;
`;

const ModalContainer = styled(Box)<{
  $borderColor: string;
  $glowColor: string;
}>`
  background: ${({ theme }) => theme.colors.ned};
  border: 1px solid ${({ $borderColor }) => $borderColor};
  border-radius: 16px;
  box-shadow:
    0 4px 12px rgba(0, 0, 0, 0.25),
    0 2px 4px rgba(0, 0, 0, 0.15),
    0 0 24px ${({ $glowColor }) => $glowColor};
  max-width: 520px;
  overflow: hidden;
  position: relative;
  width: 90%;
`;

const CardHeader = styled(Box)<{ $gradient: string }>`
  background: ${({ $gradient }) => $gradient};
  border-radius: 16px 16px 0 0;
  overflow: hidden;
  padding: 28px 24px 20px;
  position: relative;
`;

const BackgroundBadgeWrapper = styled(Box)`
  left: -45px;
  opacity: 0.15;
  position: absolute;
  top: -55px;
  z-index: 0;

  svg {
    height: 140px !important;
    -webkit-mask: linear-gradient(
      to right,
      black 0%,
      black 40%,
      transparent 85%
    );
    mask: linear-gradient(to right, black 0%, black 40%, transparent 85%);
    -webkit-mask-size: 100% 100%;
    mask-size: 100% 100%;
    position: relative;
    width: 140px !important;
    z-index: 1;
  }
`;

const LimitedOfferBadge = styled(Box)<{ $color: string }>`
  background: ${({ $color }) => `${$color}1a`};
  border: 1px solid ${({ $color }) => `${$color}55`};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  display: inline-block;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 1.5px;
  margin-bottom: 10px;
  padding: 3px 8px;
  position: relative;
  text-transform: uppercase;
  z-index: 1;
`;

const CloseButton = styled.button`
  align-items: center;
  background: transparent;
  border: none;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  justify-content: center;
  padding: 8px;
  position: absolute;
  right: 12px;
  top: 12px;
  transition: color 0.2s ease;
  z-index: 2;

  &:hover {
    color: ${({ theme }) => theme.colors.envy};
  }
`;

const Title = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 22px;
  font-weight: 700;
  line-height: 1.3;
  position: relative;
  z-index: 1;
`;

const ModalContent = styled(Flex)`
  border-top: 1px solid rgba(255, 255, 255, 0.05);
  flex-direction: column;
  gap: 12px;
  padding: 20px 24px 24px;
`;

const PlanCards = styled(Flex)`
  background: rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 10px;
  overflow: hidden;
`;

const PlanCard = styled(Flex)<{ $highlighted?: boolean }>`
  flex: 1;
  flex-direction: column;
  gap: 8px;
  padding: 14px 16px;
  ${({ $highlighted }) =>
    $highlighted && "background: rgba(255, 255, 255, 0.03);"}
`;

const PlanDivider = styled.div`
  background: rgba(255, 255, 255, 0.06);
  flex-shrink: 0;
  width: 1px;
`;

const PlanHeadlineRow = styled(Flex)`
  align-items: center;
  gap: 6px;
`;

const PlanHeadline = styled(Text)`
  color: ${({ theme }) => theme.colors.ice};
  font-size: 18px;
  font-weight: 700;
  line-height: 1;
`;

const PricingInlineRow = styled(Flex)`
  align-items: baseline;
  flex-wrap: nowrap;
  gap: 4px;
  white-space: nowrap;
`;

const PricingArrow = styled(Text)`
  color: rgba(255, 255, 255, 0.3);
  font-size: 12px;
  margin: 0 2px;
`;

const OriginalPrice = styled(Text)`
  color: rgba(255, 255, 255, 0.35);
  font-size: 14px;
  font-weight: 600;
  line-height: 1;
  text-decoration: line-through;
`;

const DiscountedPrice = styled(Text)<{ $highlighted?: boolean }>`
  color: ${({ $highlighted, theme }) =>
    $highlighted ? theme.colors.ice : theme.colors.chalk};
  font-size: 20px;
  font-weight: 700;
  line-height: 1;
`;

const PlanPeriod = styled(Text)`
  color: rgba(255, 255, 255, 0.35);
  font-size: 12px;
  font-weight: 500;
`;

const DiscountTag = styled(Text)<{ $color: string }>`
  background: ${({ $color }) => `${$color}1a`};
  border: 1px solid ${({ $color }) => `${$color}55`};
  border-radius: 4px;
  color: ${({ $color }) => $color};
  font-size: 15px;
  font-weight: 700;
  padding: 2px 7px;
`;

const PlanButton = styled.button<{ $gradient: string }>`
  align-items: center;
  background: ${({ $gradient }) => $gradient};
  border: none;
  border-radius: 6px;
  color: white;
  cursor: pointer;
  display: flex;
  font-size: 13px;
  font-weight: 600;
  height: 34px;
  justify-content: center;
  margin-top: 4px;
  transition: filter 0.2s ease;
  width: 100%;

  &:hover {
    filter: brightness(1.1);
  }

  &:active {
    filter: brightness(0.95);
  }
`;

const SecondaryButton = styled.button`
  align-items: center;
  background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.15);
  border-radius: 8px;
  color: ${({ theme }) => theme.colors.chalk};
  cursor: pointer;
  display: flex;
  font-size: 15px;
  font-weight: 600;
  height: 40px;
  justify-content: center;
  transition: all 0.2s ease;
  width: 100%;

  &:hover {
    background: rgba(255, 255, 255, 0.05);
    border-color: rgba(255, 255, 255, 0.25);
    color: ${({ theme }) => theme.colors.ice};
  }
`;

export { ExpiredSubscriptionUpsell };
